import { PrivacyNoticeConsent, SpecificUseConsent, TermOfServiceConsent } from 'domains/User';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isTaiwanPhone } from 'domains/PhoneNumber';

/* 応募フォームURL：審査番号暗号化 */
const SAMPLE = 'sample';
export const encryptExamNo = (auditionId: string, examNo: number, isSample: boolean) =>
  encodeURIComponent(btoa(`${auditionId}${examNo}${isSample ? SAMPLE : ''}`));
export const decryptExamNo = (auditionId: string, encryptedExamNo: string) => {
  const decoded = atob(decodeURIComponent(encryptedExamNo));
  const isSample = decoded.endsWith(SAMPLE);
  if (isSample) {
    const nonSampleDecoded = decoded.slice(0, -SAMPLE.length);
    return {
      isSample,
      examNo: parseInt(nonSampleDecoded.replace(auditionId, ''), 10),
    };
  }
  return {
    isSample,
    examNo: parseInt(decoded.replace(auditionId, ''), 10),
  };
};

/* オーディションフォーム: 回答値のファイルの区分 */
export const AnswerFileType = {
  CONSENT: 'consent',
  OTHER: 'other',
} as const;

type TemporaryRegistrationSchemaMessages = {
  RequiredMessage: string;
  PhoneNumberMessage: string;
  FullWidthMessage: string;
};
export const TemporaryRegistrationSchema = (messages: TemporaryRegistrationSchemaMessages) =>
  z.object({
    phoneNumber: z
      .string({
        required_error: messages.RequiredMessage,
        invalid_type_error: messages.FullWidthMessage,
      })
      .min(1, messages.RequiredMessage)
      .refine((value) => /^[0-9+]+$/.test(value), {
        message: messages.FullWidthMessage,
      })
      .refine(isValidPhoneNumber, {
        message: messages.PhoneNumberMessage,
      }),
    term_of_service_consent: TermOfServiceConsentSchema,
    privacy_notice_consent: PrivacyNoticeConsentSchema,
    specific_use_consent: SpecificUseConsentSchema,
    birthday: z.date().nullable(),
  });
export type TemporaryRegistrationSchemaType = z.infer<
  ReturnType<typeof TemporaryRegistrationSchema>
>;

type PhoneNumberSchemaMessages = {
  RequiredMessage: string;
  PhoneNumberMessage: string;
  FullWidthMessage: string;
};
export const PhoneVerificationFormSchema = (messages: PhoneNumberSchemaMessages) =>
  z.object({
    phoneNumber: z
      .string({
        required_error: messages.RequiredMessage,
        invalid_type_error: messages.FullWidthMessage,
      })
      .min(1, messages.RequiredMessage)
      .refine((value) => /^[0-9+]+$/.test(value), {
        message: messages.FullWidthMessage,
      })
      .refine(isValidPhoneNumber, {
        message: messages.PhoneNumberMessage,
      }),
  });
export type PhoneNumberSchemaType = z.infer<ReturnType<typeof PhoneVerificationFormSchema>>;

// スキーマのエラーメッセージは外部から注入する
type CreateUserFormSchemaMessages = {
  RequiredMessage: string;
  MailMessage: string;
  PhoneNumberMessage: string;
  KanaMessage: string;
  PasswordConfirmationMessage: string;
  SpecialCharacterMessage: string;
  UppercaseMessage: string;
  LowercaseMessage: string;
  ConsentRequiredMessage: string;
  MinLengthMessage: (min: number) => string;
  MaxLengthMessage: (max: number) => string;
  LengthMessage: (length: number) => string;
  FullWidthMessage: string;
  NoFullWidthMessage: string;
  PasswordNumericMessage: string;
};

// 全角カタカナの正規表現
const ZennkakuKanaRegex = /^[ァ-ヴー]+$/;

const TermOfServiceConsentSchema = z.enum([TermOfServiceConsent.Yes, TermOfServiceConsent.No]);
const PrivacyNoticeConsentSchema = z.enum([PrivacyNoticeConsent.Yes, PrivacyNoticeConsent.No]);
const SpecificUseConsentSchema = z.enum([SpecificUseConsent.Yes, SpecificUseConsent.No]);

export const CreateUserFormSchema = (messages: CreateUserFormSchemaMessages) =>
  z
    .object({
      phoneNumber: z
        .string({
          required_error: messages.RequiredMessage,
          invalid_type_error: messages.FullWidthMessage,
        })
        .min(1, messages.RequiredMessage)
        .refine((value) => /^[0-9+]+$/.test(value), {
          message: messages.FullWidthMessage,
        })
        .refine(isValidPhoneNumber, {
          message: messages.PhoneNumberMessage,
        }),
      id: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.LengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        ),
      password: z
        .string()
        .min(1, messages.RequiredMessage)
        .min(12, messages.MinLengthMessage(12))
        .max(100, messages.MaxLengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        )
        .regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/, messages.SpecialCharacterMessage)
        .regex(/[A-Z]/, messages.UppercaseMessage)
        .regex(/[a-z]/, messages.LowercaseMessage)
        .regex(/[0-9]/, messages.PasswordNumericMessage),
      passwordConfirmation: z.string().min(1, messages.RequiredMessage),
    })
    .superRefine(({ password, passwordConfirmation }, ctx) => {
      if (password !== passwordConfirmation) {
        ctx.addIssue({
          path: ['passwordConfirmation'],
          code: 'custom',
          message: messages.PasswordConfirmationMessage,
        });
      }
    });
export type CreateUserFormSchemaType = z.infer<ReturnType<typeof CreateUserFormSchema>>;

type CreateGroupFormSchemaMessages = {
  RequiredMessage: string;
  PhoneNumberMessage: string;
  FullWidthMessage: string;
  KanaMessage: string;
  MinLengthMessage: (min: number) => string;
  MaxLengthMessage: (max: number) => string;
};
export const CreateGroupFormSchema = (messages: CreateGroupFormSchemaMessages) =>
  z.object({
    name: z.string().min(1, messages.RequiredMessage).max(100, messages.MaxLengthMessage(100)),
    birthday: z.date().nullable(),
    phoneNumber: z
      .string({
        required_error: messages.RequiredMessage,
        invalid_type_error: messages.FullWidthMessage,
      })
      .min(1, messages.RequiredMessage)
      .refine((value) => /^[0-9+]+$/.test(value), {
        message: messages.FullWidthMessage,
      })
      .refine(isValidPhoneNumber, {
        message: messages.PhoneNumberMessage,
      }),
  });
export type CreateGroupFormSchemaType = z.infer<ReturnType<typeof CreateGroupFormSchema>>;

export const CreateUserDetailFormSchema = (
  messages: CreateUserFormSchemaMessages,
  isMinor: boolean,
  phoneNumber: string,
  currentLang: string
) =>
  z
    .object({
      firstName: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100)),
      lastName: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100)),
      firstNameKana:
        currentLang === 'ja'
          ? z
              .string()
              .min(1, messages.RequiredMessage)
              .regex(ZennkakuKanaRegex, messages.KanaMessage)
              .max(100, messages.MaxLengthMessage(100))
          : z.string().max(100, messages.MaxLengthMessage(100)),
      lastNameKana:
        currentLang === 'ja'
          ? z
              .string()
              .min(1, messages.RequiredMessage)
              .regex(ZennkakuKanaRegex, messages.KanaMessage)
              .max(100, messages.MaxLengthMessage(100))
          : z.string().max(100, messages.MaxLengthMessage(100)),
      birthday: z.date(),
      mailAddress: z
        .string()
        .max(100, messages.MaxLengthMessage(100))
        .email(messages.MailMessage)
        .or(z.literal('')),
      parentName: isMinor
        ? z.string().min(1, messages.RequiredMessage).max(100, messages.MaxLengthMessage(100))
        : z.string().max(100, messages.MaxLengthMessage(100)).or(z.literal('')),
      parentPhoneNumber: isMinor
        ? z
            .string({
              required_error: messages.RequiredMessage,
              invalid_type_error: messages.FullWidthMessage,
            })
            .min(1, messages.RequiredMessage)
            .refine((value) => /^[0-9+]+$/.test(value), {
              message: messages.FullWidthMessage,
            })
            .refine(isValidPhoneNumber, {
              message: messages.PhoneNumberMessage,
            })
        : z.string().or(z.literal('')),
    })
    .superRefine((data, ctx) => {
      // 台湾の電話番号の場合、メールアドレスは必須
      if (isTaiwanPhone(phoneNumber) && !data.mailAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.RequiredMessage,
          path: ['mailAddress'],
        });
      }
    });
export type CreateUserDetailFormSchemaType = z.infer<ReturnType<typeof CreateUserDetailFormSchema>>;

type SignUpConfirmSchemaMessages = {
  LengthMessage: (length: number) => string;
  RequiredMessage: string;
  NumericMessage: string;
};
export const SignUpConfirmSchema = (messages: SignUpConfirmSchemaMessages) =>
  z.object({
    mfaCode: z
      .string()
      .min(1, messages.RequiredMessage)
      .length(6, messages.LengthMessage(6))
      .regex(/^[0-9]+$/, messages.NumericMessage),
  });
export type SignUpConfirmSchemaType = z.infer<ReturnType<typeof SignUpConfirmSchema>>;

type MailAddressSchemaMessages = {
  MaxLengthMessage: (length: number) => string;
  MinLengthMessage: (length: number) => string;
  RequiredMessage: string;
  MailMessage: string;
};
export const MailAddressSchema = (messages: MailAddressSchemaMessages) =>
  z.object({
    mailAddress: z
      .string()
      .min(1, messages.RequiredMessage)
      .max(100, messages.MaxLengthMessage(100))
      .email(messages.MailMessage),
    privacyPolicyConsent: z.boolean().refine((value) => value === true, messages.RequiredMessage),
  });
export type MailAddressSchemaType = z.infer<ReturnType<typeof MailAddressSchema>>;

// ファイルサイズの上限(500MB)
export const MAX_FILE_SIZE_IN_BYTES = 500 * 1024 * 1024;

type CreateUploadConsentFormSchemaMessages = {
  RequiredMessage: string;
  MinLengthMessage: (min: number) => string;
  MaxLengthMessage: (max: number) => string;
  FileRequiredMessage: string;
  FileSizeMessage: string;
  PhoneNumberMessage: string;
  FileExtensionMessage: string;
};
const ALLOWED_FILE_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.gif',
  '.svg',
  '.webp',
  '.bmp',
];
const hasValidFileExtension = (
  file: File,
  allowedExtensions: string[] = ALLOWED_FILE_EXTENSIONS
) => {
  const fileName = file.name.toLowerCase();
  return allowedExtensions.some((ext) => fileName.endsWith(ext));
};
export const CreateUploadConsentFormSchema = (
  messages: CreateUploadConsentFormSchemaMessages,
  representative: boolean,
  specific_use_file_required: string,
  privacy_policy_file_required: string,
  audition_terms_file_required: string
) =>
  z.object({
    term_of_service_file: z
      .instanceof(File, { message: messages.FileRequiredMessage })
      .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
      .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage),
    privacy_notice_file: z
      .instanceof(File, { message: messages.FileRequiredMessage })
      .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
      .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage),
    specific_use_file:
      specific_use_file_required === '1'
        ? z
            .instanceof(File, { message: messages.FileRequiredMessage })
            .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
            .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage)
        : z.instanceof(File).optional(),
    privacy_policy_file:
      privacy_policy_file_required === '1'
        ? z
            .instanceof(File, { message: messages.FileRequiredMessage })
            .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
            .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage)
        : z.instanceof(File).optional(),
    audition_terms_file:
      audition_terms_file_required === '1'
        ? z
            .instanceof(File, { message: messages.FileRequiredMessage })
            .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
            .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage)
        : z.instanceof(File).optional(),
    evidence_file: z
      .instanceof(File, { message: messages.FileRequiredMessage })
      .refine((file) => file.size <= MAX_FILE_SIZE_IN_BYTES, messages.FileSizeMessage)
      .refine((file) => hasValidFileExtension(file), messages.FileExtensionMessage),
  });
export type CreateUploadConsentFormSchemaType = z.infer<
  ReturnType<typeof CreateUploadConsentFormSchema>
>;

export enum GroupAp {
  Individual = '0',
  Group = '1',
}
type CreateGroupConfirmFormSchemaMessages = {
  GroupSizeTooSmallMessage: string;
};
export const CreateGroupConfirmFormSchema = (messages: CreateGroupConfirmFormSchemaMessages) =>
  z
    .object({
      group_ap: z.enum([GroupAp.Individual, GroupAp.Group]),
      member_number: z.number(),
    })
    .superRefine((data, ctx) => {
      const { group_ap, member_number } = data;
      if (group_ap === GroupAp.Group && member_number < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.GroupSizeTooSmallMessage,
          path: ['member_number'],
        });
      }
    });
export type CreateGroupConfirmFormSchemaType = z.infer<
  ReturnType<typeof CreateGroupConfirmFormSchema>
>;
